import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, UserPlus, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-primary);
  padding: var(--spacing-lg);
`;

const RegisterCard = styled(motion.div)`
  background: var(--color-background);
  border-radius: var(--border-radius-xl);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-2xl);
  width: 100%;
  max-width: 400px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: var(--color-text-muted);
  text-decoration: none;
  font-size: 0.875rem;
  margin-bottom: var(--spacing-lg);
  transition: color var(--animation-duration) ease;
  
  &:hover {
    color: var(--color-primary);
    text-decoration: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  text-align: center;
  margin-bottom: var(--spacing-2xl);
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const FormGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
  font-size: 0.875rem;
`;

const InputWrapper = styled.div`
  position: relative;
  
  .icon {
    position: absolute;
    left: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    width: 18px;
    height: 18px;
  }
  
  .toggle-icon {
    position: absolute;
    right: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    cursor: pointer;
    width: 18px;
    height: 18px;
    transition: color var(--animation-duration) ease;
    
    &:hover {
      color: var(--color-primary);
    }
  }
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-md) var(--spacing-md) var(--spacing-md) 48px;
  border: 2px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  transition: all var(--animation-duration) ease;
  background: var(--color-background);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &.error {
    border-color: #ef4444;
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: var(--spacing-xs);
  display: block;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: var(--spacing-md);
  background: var(--gradient-primary);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  min-height: 48px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: var(--spacing-lg) 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--color-border);
  }
  
  span {
    padding: 0 var(--spacing-md);
    color: var(--color-text-muted);
    font-size: 0.875rem;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  
  a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PasswordStrength = styled.div`
  margin-top: var(--spacing-sm);
  
  .strength-bar {
    height: 4px;
    background: var(--color-border);
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: var(--spacing-xs);
    
    .strength-fill {
      height: 100%;
      transition: all var(--animation-duration) ease;
      border-radius: 2px;
    }
  }
  
  .strength-text {
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }
`;

const Register = () => {
  const { register: registerUser, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    watch
  } = useForm();

  const watchPassword = watch('password', '');

  // 如果已经登录，重定向到首页或返回页面
  useEffect(() => {
    if (isAuthenticated) {
      const returnUrl = new URLSearchParams(location.search).get('returnUrl');
      navigate(returnUrl || '/', { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // 计算密码强度
  useEffect(() => {
    const calculatePasswordStrength = (password) => {
      let strength = 0;
      if (password.length >= 6) strength += 25;
      if (password.length >= 8) strength += 25;
      if (/[A-Z]/.test(password)) strength += 25;
      if (/[0-9]/.test(password)) strength += 25;
      return strength;
    };

    setPasswordStrength(calculatePasswordStrength(watchPassword));
  }, [watchPassword]);

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      setError('confirmPassword', {
        type: 'manual',
        message: '两次输入的密码不一致'
      });
      return;
    }

    try {
      const result = await registerUser(data.username, data.email, data.password);
      
      if (result.success) {
        const returnUrl = new URLSearchParams(location.search).get('returnUrl');
        navigate(returnUrl || '/', { replace: true });
      } else {
        setError('root', {
          type: 'manual',
          message: result.error || '注册失败'
        });
      }
    } catch (error) {
      console.error('Register error:', error);
      setError('root', {
        type: 'manual',
        message: '注册过程中发生错误'
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const getStrengthColor = () => {
    if (passwordStrength < 25) return '#ef4444';
    if (passwordStrength < 50) return '#f59e0b';
    if (passwordStrength < 75) return '#eab308';
    return '#10b981';
  };

  const getStrengthText = () => {
    if (passwordStrength < 25) return '弱';
    if (passwordStrength < 50) return '一般';
    if (passwordStrength < 75) return '良好';
    return '强';
  };

  return (
    <RegisterContainer>
      <RegisterCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <BackButton to="/">
          <ArrowLeft />
          返回首页
        </BackButton>
        
        <Title>创建账户</Title>
        <Subtitle>加入我们，开始您的历史学习之旅</Subtitle>
        
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="username">用户名</Label>
            <InputWrapper>
              <User className="icon" />
              <Input
                id="username"
                type="text"
                placeholder="请输入用户名"
                className={errors.username ? 'error' : ''}
                {...register('username', {
                  required: '请输入用户名',
                  minLength: {
                    value: 3,
                    message: '用户名长度至少3个字符'
                  },
                  maxLength: {
                    value: 20,
                    message: '用户名长度不能超过20个字符'
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_\u4e00-\u9fa5]+$/,
                    message: '用户名只能包含字母、数字、下划线和中文'
                  }
                })}
              />
            </InputWrapper>
            {errors.username && (
              <ErrorMessage>{errors.username.message}</ErrorMessage>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="email">邮箱地址</Label>
            <InputWrapper>
              <Mail className="icon" />
              <Input
                id="email"
                type="email"
                placeholder="请输入您的邮箱地址"
                className={errors.email ? 'error' : ''}
                {...register('email', {
                  required: '请输入邮箱地址',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: '请输入有效的邮箱地址'
                  }
                })}
              />
            </InputWrapper>
            {errors.email && (
              <ErrorMessage>{errors.email.message}</ErrorMessage>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="password">密码</Label>
            <InputWrapper>
              <Lock className="icon" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="请输入密码"
                className={errors.password ? 'error' : ''}
                {...register('password', {
                  required: '请输入密码',
                  minLength: {
                    value: 6,
                    message: '密码长度至少6个字符'
                  }
                })}
              />
              {showPassword ? (
                <EyeOff className="toggle-icon" onClick={togglePasswordVisibility} />
              ) : (
                <Eye className="toggle-icon" onClick={togglePasswordVisibility} />
              )}
            </InputWrapper>
            {watchPassword && (
              <PasswordStrength>
                <div className="strength-bar">
                  <div 
                    className="strength-fill"
                    style={{
                      width: `${passwordStrength}%`,
                      backgroundColor: getStrengthColor()
                    }}
                  />
                </div>
                <div className="strength-text">
                  密码强度: {getStrengthText()}
                </div>
              </PasswordStrength>
            )}
            {errors.password && (
              <ErrorMessage>{errors.password.message}</ErrorMessage>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="confirmPassword">确认密码</Label>
            <InputWrapper>
              <Lock className="icon" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="请再次输入密码"
                className={errors.confirmPassword ? 'error' : ''}
                {...register('confirmPassword', {
                  required: '请确认密码'
                })}
              />
              {showConfirmPassword ? (
                <EyeOff className="toggle-icon" onClick={toggleConfirmPasswordVisibility} />
              ) : (
                <Eye className="toggle-icon" onClick={toggleConfirmPasswordVisibility} />
              )}
            </InputWrapper>
            {errors.confirmPassword && (
              <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
            )}
          </FormGroup>
          
          {errors.root && (
            <ErrorMessage>{errors.root.message}</ErrorMessage>
          )}
          
          <SubmitButton type="submit" disabled={isSubmitting || loading}>
            {isSubmitting || loading ? (
              <LoadingSpinner />
            ) : (
              <>
                <UserPlus />
                注册
              </>
            )}
          </SubmitButton>
        </Form>
        
        <Divider>
          <span>已有账户？</span>
        </Divider>
        
        <LoginLink>
          <Link to={`/login${location.search}`}>立即登录</Link>
        </LoginLink>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register;